import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './QuizGame.css';

const MultiplayerLobbyKBC = ({ 
  userRole, 
  userName, 
  userClass, 
  userEmail, 
  userId, 
  onStartGame, 
  onExit 
}) => {
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [gameRequests, setGameRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [challengeConfig, setChallengeConfig] = useState({
    subject: '',
    class: userClass || '1',
    difficulty: 'easy'
  });
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [availableOptions, setAvailableOptions] = useState({ subjects: [], classes: [] });

  const isTeacher = ['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(userRole);

  // Update online status
  useEffect(() => {
    const updateOnlineStatus = async () => {
      try {
        if (!userName || !userRole) {
          console.log('Missing userName or userRole:', { userName, userRole });
          return;
        }
        
        const uniqueId = userId || userName + '_' + userRole;
        const displayName = userName || userEmail || `User_${uniqueId}`;
        console.log('Registering online:', displayName, userRole);
        
        const response = await fetch('/api/users/online-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            userId: uniqueId, 
            name: displayName, 
            email: userEmail,
            role: userRole, 
            class: userClass 
          })
        });
        
        if (!response.ok) {
          console.error('Failed to register online status:', response.status);
        }
      } catch (error) {
        console.error('Failed to update online status:', error);
      }
    };

    const fetchOnlinePlayers = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/games/online-players');
        if (response.ok) {
          const players = await response.json();
          console.log('Fetched players:', players);
          const otherPlayers = players.filter(player => player.name !== userName);
          setOnlinePlayers(otherPlayers);
        }
      } catch (error) {
        console.error('Error fetching online players:', error);
        setOnlinePlayers([]);
      } finally {
        setLoading(false);
      }
    };

    updateOnlineStatus();
    fetchOnlinePlayers();
    
    const statusInterval = setInterval(updateOnlineStatus, 10000);
    const fetchInterval = setInterval(fetchOnlinePlayers, 2000);
    
    return () => {
      clearInterval(statusInterval);
      clearInterval(fetchInterval);
    };
  }, [userName, userRole, userClass, userEmail, userId]);

  // Fetch available options
  useEffect(() => {
    const fetchAvailableOptions = async () => {
      try {
        const response = await fetch('/api/quiz/available-options');
        if (response.ok) {
          const options = await response.json();
          setAvailableOptions(options);
        }
      } catch (error) {
        console.error('Error fetching available options:', error);
      }
    };
    
    fetchAvailableOptions();
  }, []);

  // Fetch game requests
  useEffect(() => {
    const fetchGameRequests = async () => {
      try {
        const response = await fetch(`/api/kbc-challenges/${userName}`);
        if (response.ok) {
          const requests = await response.json();
          setGameRequests(requests);
        }
      } catch (error) {
        console.error('Error fetching game requests:', error);
      }
    };

    if (userName) {
      fetchGameRequests();
      const interval = setInterval(fetchGameRequests, 3000);
      return () => clearInterval(interval);
    }
  }, [userName]);

  const canChallenge = (player) => {
    if (isTeacher) {
      // Teachers can challenge anyone
      return true;
    } else {
      // Students can only challenge other students
      return player.role === 'student';
    }
  };

  const openChallengeModal = (opponent) => {
    setSelectedOpponent(opponent);
    setShowChallengeModal(true);
    setChallengeConfig({
      subject: '',
      class: userClass || '1',
      difficulty: 'easy'
    });
  };

  const fetchQuestions = async () => {
    try {
      const response = await axios.get('/api/quiz/questions', {
        params: {
          subject: challengeConfig.subject,
          class: challengeConfig.class,
          limit: 15 // Get extra questions for selection
        }
      });
      
      if (response.data && response.data.length >= 12) {
        setAvailableQuestions(response.data);
        return response.data.slice(0, 12); // Use first 12 questions
      } else {
        alert('Not enough questions available for this subject and class. Need at least 12 questions.');
        return null;
      }
    } catch (error) {
      alert('Error fetching questions. Please try again.');
      return null;
    }
  };

  const sendChallenge = async () => {
    if (!challengeConfig.subject) {
      alert('Please select a subject');
      return;
    }

    const questions = await fetchQuestions();
    if (!questions) return;

    try {
      setSentRequests(prev => [...prev, selectedOpponent.name]);
      
      const response = await fetch('/api/kbc-challenges/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromPlayer: userName,
          fromRole: userRole,
          fromClass: userClass,
          toPlayer: selectedOpponent.name,
          toRole: selectedOpponent.role,
          toClass: selectedOpponent.class,
          gameConfig: challengeConfig,
          questions: questions
        })
      });
      
      if (response.ok) {
        const { requestId } = await response.json();
        alert(`KBC Challenge sent to ${selectedOpponent.name}! Waiting for acceptance...`);
        setShowChallengeModal(false);
        setSelectedOpponent(null);
        
        // Start polling for challenge acceptance
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await fetch(`/api/kbc-challenges/status/${requestId}`);
            if (statusResponse.ok) {
              const status = await statusResponse.json();
              if (status.accepted) {
                clearInterval(pollInterval);
                // Start the game for challenger
                onStartGame({
                  mode: 'multiplayer-kbc',
                  gameSession: status.gameSession
                });
              } else if (status.rejected) {
                clearInterval(pollInterval);
                alert('Challenge was rejected.');
                setSentRequests(prev => prev.filter(name => name !== selectedOpponent.name));
              }
            }
          } catch (error) {
            console.error('Error polling challenge status:', error);
          }
        }, 2000);
        
        // Stop polling after 2 minutes
        setTimeout(() => clearInterval(pollInterval), 120000);
      }
    } catch (error) {
      console.error('Error sending challenge:', error);
      setSentRequests(prev => prev.filter(name => name !== selectedOpponent.name));
      alert('Failed to send challenge. Please try again.');
    }
  };

  const acceptChallenge = async (request) => {
    try {
      const response = await fetch('/api/kbc-challenges/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId: request._id,
          challenger: request.fromPlayer,
          challenged: userName,
          questions: request.questions,
          gameConfig: request.gameConfig
        })
      });
      
      if (response.ok) {
        const { gameSession } = await response.json();
        onStartGame({
          mode: 'multiplayer-kbc',
          gameSession: {
            ...gameSession,
            players: [
              { name: request.fromPlayer, role: request.fromRole },
              { name: userName, role: userRole }
            ],
            questions: request.questions,
            config: request.gameConfig
          }
        });
      }
    } catch (error) {
      console.error('Error accepting challenge:', error);
      alert('Failed to accept challenge. Please try again.');
    }
  };

  const rejectChallenge = async (requestId) => {
    try {
      await fetch(`/api/kbc-challenges/${requestId}`, {
        method: 'DELETE'
      });
      setGameRequests(prev => prev.filter(req => req._id !== requestId));
    } catch (error) {
      console.error('Error rejecting challenge:', error);
    }
  };

  return (
    <div className="quiz-game">
      <div className="prize-ladder-container" style={{ maxWidth: '600px' }}>
        <h2>🎮 Multiplayer KBC</h2>
        
        {/* Challenge Requests */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ color: '#000' }}>🎯 Challenge Requests ({gameRequests.length})</h3>
          {gameRequests.length === 0 ? (
            <div style={{
              background: 'rgba(107, 70, 193, 0.1)',
              padding: '15px',
              borderRadius: '10px',
              textAlign: 'center',
              color: '#666'
            }}>
              No pending challenges
            </div>
          ) : (
            gameRequests.map((request, index) => (
              <div key={index} style={{
                background: 'rgba(16, 185, 129, 0.1)',
                padding: '15px',
                borderRadius: '10px',
                margin: '10px 0',
                border: '2px solid #10b981'
              }}>
                <div style={{ marginBottom: '10px', color: '#000', fontWeight: 'bold' }}>
                  🏆 {request.fromPlayer} challenges you to KBC!
                </div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                  Subject: {request.gameConfig?.subject} | Class: {request.gameConfig?.class} | 
                  Questions: {request.questions?.length || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#999', marginBottom: '15px' }}>
                  {request.fromRole} from Class {request.fromClass}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    onClick={() => acceptChallenge(request)}
                    className="continue-btn"
                    style={{ flex: 1, padding: '12px', fontSize: '16px', fontWeight: 'bold' }}
                  >
                    ✅ Accept Challenge
                  </button>
                  <button 
                    onClick={() => rejectChallenge(request._id)}
                    className="quit-btn"
                    style={{ flex: 1, padding: '12px', fontSize: '16px' }}
                  >
                    ❌ Decline
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Online Players */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ color: '#000' }}>👥 Players Online ({onlinePlayers.length})</h3>
          
          {loading ? (
            <div style={{
              background: 'rgba(107, 70, 193, 0.1)',
              padding: '20px',
              borderRadius: '10px',
              textAlign: 'center',
              color: '#666'
            }}>
              🔄 Loading online players...
            </div>
          ) : onlinePlayers.length === 0 ? (
            <div style={{
              background: 'rgba(107, 70, 193, 0.1)',
              padding: '20px',
              borderRadius: '10px',
              textAlign: 'center',
              color: '#666'
            }}>
              No other players online right now. Try again later!
            </div>
          ) : (
            <div>
              {/* Teachers */}
              {onlinePlayers.filter(p => ['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(p.role)).length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#000', marginBottom: '10px' }}>👨🏫 Teachers Online</h4>
                  {onlinePlayers
                    .filter(p => ['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(p.role))
                    .map((player, index) => (
                      <div key={index} style={{
                        background: 'rgba(107, 70, 193, 0.1)',
                        padding: '15px',
                        borderRadius: '10px',
                        margin: '8px 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ fontWeight: 'bold', color: '#000' }}>{player.name}</div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            {player.role} • Class {player.class}
                          </div>
                          <div style={{ fontSize: '12px', color: '#10b981' }}>
                            🟢 Online
                          </div>
                        </div>
                        
                        {canChallenge(player) && !sentRequests.includes(player.name) && (
                          <button 
                            onClick={() => openChallengeModal(player)}
                            className="continue-btn"
                            style={{ padding: '8px 16px', fontSize: '14px' }}
                          >
                            🏆 Challenge
                          </button>
                        )}
                        
                        {sentRequests.includes(player.name) && (
                          <div style={{ 
                            background: '#fbbf24', 
                            color: 'white', 
                            padding: '8px 16px', 
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}>
                            ⏳ Challenge Sent
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {/* Students */}
              {onlinePlayers.filter(p => p.role === 'student').length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#000', marginBottom: '10px' }}>👨🎓 Students Online</h4>
                  {onlinePlayers
                    .filter(p => p.role === 'student')
                    .map((player, index) => (
                      <div key={index} style={{
                        background: 'rgba(16, 185, 129, 0.1)',
                        padding: '15px',
                        borderRadius: '10px',
                        margin: '8px 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ fontWeight: 'bold', color: '#000' }}>{player.name}</div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            Student • Class {player.class}
                          </div>
                          <div style={{ fontSize: '12px', color: '#10b981' }}>
                            🟢 Online
                          </div>
                        </div>
                        
                        {canChallenge(player) && !sentRequests.includes(player.name) && (
                          <button 
                            onClick={() => openChallengeModal(player)}
                            className="continue-btn"
                            style={{ padding: '8px 16px', fontSize: '14px' }}
                          >
                            🏆 Challenge
                          </button>
                        )}
                        
                        {sentRequests.includes(player.name) && (
                          <div style={{ 
                            background: '#fbbf24', 
                            color: 'white', 
                            padding: '8px 16px', 
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}>
                            ⏳ Challenge Sent
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div style={{ fontSize: '12px', color: '#999', textAlign: 'center', marginTop: '10px' }}>
          🟢 Live • {onlinePlayers.length} players online
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onExit} className="quit-btn" style={{ flex: 1 }}>
            Back to Games
          </button>
        </div>
      </div>

      {/* Challenge Modal */}
      {showChallengeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginBottom: '20px', textAlign: 'center' }}>
              🏆 Challenge {selectedOpponent?.name}
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Subject: *
              </label>
              <select 
                value={challengeConfig.subject} 
                onChange={(e) => setChallengeConfig({...challengeConfig, subject: e.target.value})}
                style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
              >
                <option value="">Select Subject</option>
                {availableOptions.subjects.filter(subject => subject !== 'Mathematics').map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Class:
              </label>
              <select 
                value={challengeConfig.class} 
                onChange={(e) => setChallengeConfig({...challengeConfig, class: e.target.value})}
                style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
              >
                {availableOptions.classes.filter(cls => cls !== '2').map(cls => (
                  <option key={cls} value={cls}>{cls.toString().startsWith('Class') ? cls : `Class ${cls}`}</option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Difficulty:
              </label>
              <select 
                value={challengeConfig.difficulty} 
                onChange={(e) => setChallengeConfig({...challengeConfig, difficulty: e.target.value})}
                style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={sendChallenge}
                className="continue-btn"
                style={{ flex: 1, padding: '12px' }}
                disabled={!challengeConfig.subject}
              >
                Send Challenge
              </button>
              <button 
                onClick={() => setShowChallengeModal(false)}
                className="quit-btn"
                style={{ flex: 1, padding: '12px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiplayerLobbyKBC;